import React from 'react'
import type { Block, Variable } from '../../types'
import type { ChartType } from '../../../renderer/blocks/ChartBlock'
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select'
import { PropertyLabel, PropertyInput, ToggleSwitch } from './shared'
import { Plus, X, BarChart3, TrendingUp, PieChart, AreaChart as AreaChartIcon, AlignLeft, Layers, Circle } from 'lucide-react'

interface ChartPropertiesProps {
  block: Block
  blocks: Block[]
  variables: Variable[]
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
  onAddVariable: (name?: string) => string
}

const CHART_TYPES: { type: ChartType; label: string; icon: React.ReactNode }[] = [
  { type: 'bar', label: 'Bar', icon: <BarChart3 className="h-4 w-4" /> },
  { type: 'horizontal-bar', label: 'H-Bar', icon: <AlignLeft className="h-4 w-4" /> },
  { type: 'stacked-bar', label: 'Stacked', icon: <Layers className="h-4 w-4" /> },
  { type: 'line', label: 'Line', icon: <TrendingUp className="h-4 w-4" /> },
  { type: 'area', label: 'Area', icon: <AreaChartIcon className="h-4 w-4" /> },
  { type: 'donut', label: 'Donut', icon: <PieChart className="h-4 w-4" /> },
  { type: 'pie', label: 'Pie', icon: <Circle className="h-4 w-4" /> },
]

export default function ChartProperties({ block, blocks, variables, onUpdateBlock, onAddVariable }: ChartPropertiesProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const chartType = (config.chartType as ChartType) || 'bar'
  const categories = (config.categories as string[]) || ['OCT', 'NOV', 'DEC']
  const baselines = (config.baselines as number[]) || [75, 60, 85]
  const dataMode = (config.dataMode as string) || 'ratio'
  const categorySources = (config.categorySources as string[]) || []

  // Include poll-derived variables in the dropdown
  const allVariables = React.useMemo(() => {
    const extra: Variable[] = []
    for (const b of blocks) {
      if (b.type === 'poll' && b.variable) {
        const cfg = (b.config || {}) as Record<string, unknown>
        const opts = (cfg.options as { label: string; value: string }[]) || []
        extra.push({ name: `${b.variable}_total`, type: 'slider', default: 0 })
        for (const opt of opts) {
          extra.push({ name: `${b.variable}_${opt.value}`, type: 'slider', default: 0 })
        }
      }
    }
    return extra.length > 0 ? [...variables, ...extra] : variables
  }, [variables, blocks])

  const updateConfig = (updates: Record<string, unknown>) => {
    onUpdateBlock(block.id, { config: { ...config, ...updates } })
  }

  const handleCreateVariable = () => {
    const name = onAddVariable()
    onUpdateBlock(block.id, { source: name })
  }

  return (
    <div className="space-y-6">
      {/* Chart Type */}
      <div className="space-y-2">
        <PropertyLabel>Chart Type</PropertyLabel>
        <div className="grid grid-cols-4 gap-1.5" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          {CHART_TYPES.map((ct) => (
            <button
              key={ct.type}
              onClick={() => updateConfig({ chartType: ct.type })}
              className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all text-xs font-medium ${
                chartType === ct.type
                  ? 'border-[#5048e5] bg-[#5048e5]/5 text-[#5048e5]'
                  : 'border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700'
              }`}
            >
              {ct.icon}
              {ct.label}
            </button>
          ))}
        </div>
      </div>

      {/* Data Mode */}
      <div className="space-y-2">
        <PropertyLabel>Data Source</PropertyLabel>
        <div className="grid grid-cols-2 gap-1.5">
          {([
            { value: 'ratio', label: 'Single Variable' },
            { value: 'variables', label: 'Per Category' },
          ] as const).map((opt) => (
            <button
              key={opt.value}
              onClick={() => updateConfig({ dataMode: opt.value })}
              className={`py-2 px-3 rounded-lg border-2 text-xs font-medium transition-all ${
                dataMode === opt.value
                  ? 'border-[#5048e5] bg-[#5048e5]/5 text-[#5048e5]'
                  : 'border-slate-200 text-slate-500 hover:border-slate-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {dataMode === 'ratio' ? (
        /* Single source variable (ratio mode) */
        <div className="space-y-2">
          <PropertyLabel>Source</PropertyLabel>
          <div className="flex gap-2">
            {variables.length > 0 ? (
              <div className="flex-1">
                <Select value={block.source || ''} onValueChange={(val) => onUpdateBlock(block.id, { source: val })}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 rounded-lg">
                    <SelectValue placeholder="Select a variable" />
                  </SelectTrigger>
                  <SelectContent>
                    {variables.filter((v) => v.name).map((v) => (
                      <SelectItem key={v.name} value={v.name}>
                        @{v.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <p className="flex-1 text-xs text-slate-400 italic py-2">No variables yet</p>
            )}
            <button
              onClick={handleCreateVariable}
              className="shrink-0 p-2.5 bg-[#5048e5] text-white rounded-lg hover:bg-[#5048e5]/90 transition-colors"
              title="Create a new variable"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        /* Per-category variable sources */
        <div className="space-y-2">
          <PropertyLabel>Category Sources</PropertyLabel>
          {categories.map((cat, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs text-slate-400 w-16 shrink-0 truncate">{cat}</span>
              <div className="flex-1">
                <Select
                  value={categorySources[i] || ''}
                  onValueChange={(val) => {
                    const newSources = [...categorySources]
                    newSources[i] = val
                    updateConfig({ categorySources: newSources })
                  }}
                >
                  <SelectTrigger className="bg-slate-50 border-slate-200 rounded-lg h-8 text-xs">
                    <SelectValue placeholder="Variable..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allVariables.filter((v) => v.name).map((v) => (
                      <SelectItem key={v.name} value={v.name}>
                        @{v.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
          <p className="text-[10px] text-slate-400">Each category reads its value from its own variable.</p>
        </div>
      )}

      {/* Label */}
      <div className="space-y-2">
        <PropertyLabel>Label</PropertyLabel>
        <PropertyInput
          value={block.label || ''}
          onChange={(e) => onUpdateBlock(block.id, { label: e.target.value })}
        />
      </div>

      {/* Categories */}
      <div className="space-y-2">
        <PropertyLabel>Categories</PropertyLabel>
        {categories.map((cat, i) => (
          <div key={i} className="flex items-center gap-2">
            <PropertyInput
              value={cat}
              onChange={(e) => {
                const newCats = [...categories]
                newCats[i] = e.target.value
                updateConfig({ categories: newCats })
              }}
              placeholder={`Category ${i + 1}`}
            />
            <button
              onClick={() => {
                const newCats = [...categories]
                const newBaselines = [...baselines]
                newCats.splice(i, 1)
                newBaselines.splice(i, 1)
                updateConfig({ categories: newCats, baselines: newBaselines })
              }}
              className="p-1 hover:bg-slate-100 rounded"
            >
              <X className="h-3.5 w-3.5 text-slate-400" />
            </button>
          </div>
        ))}
        <button
          onClick={() => updateConfig({ categories: [...categories, 'NEW'], baselines: [...baselines, 50] })}
          className="flex items-center gap-1 text-xs text-[#5048e5] hover:text-[#5048e5]/80 font-medium"
        >
          <Plus className="h-3.5 w-3.5" /> Add Category
        </button>
      </div>

      {/* Baseline Values — only in ratio mode */}
      {dataMode === 'ratio' && (
        <div className="space-y-2">
          <PropertyLabel>Baseline Values (%)</PropertyLabel>
          {baselines.map((val, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs text-slate-400 w-10 shrink-0">{categories[i] || `#${i + 1}`}</span>
              <PropertyInput
                type="number"
                min={0}
                max={100}
                value={val}
                onChange={(e) => {
                  const newBaselines = [...baselines]
                  newBaselines[i] = Math.max(0, Math.min(100, Number(e.target.value)))
                  updateConfig({ baselines: newBaselines })
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <PropertyLabel>Show Legend</PropertyLabel>
          <ToggleSwitch
            checked={(config.showLegend as boolean) ?? true}
            onChange={(v) => updateConfig({ showLegend: v })}
          />
        </div>
        {((config.showLegend as boolean) ?? true) && dataMode === 'ratio' && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="size-2 bg-slate-200 rounded-full shrink-0" />
              <PropertyInput
                value={(config.legendBaseline as string) || 'Baseline'}
                onChange={(e) => updateConfig({ legendBaseline: e.target.value })}
                placeholder="Baseline"
              />
            </div>
            <div className="flex items-center gap-2">
              <div className="size-2 bg-[#5048e5] rounded-full shrink-0" />
              <PropertyInput
                value={(config.legendProjected as string) || 'Projected'}
                onChange={(e) => updateConfig({ legendProjected: e.target.value })}
                placeholder="Projected"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
